import { useQuery } from '@tanstack/react-query';

import { getTeamsFindOptions } from 'client/@tanstack/react-query.gen';
import type {
  FundType,
  GetTeamMembersResponse,
  GetTeamResponse,
  GetTeamsFindData,
  GetTeamsFindResponse,
} from 'client/types.gen';
import { getCompanyTypeRoles } from 'helpers/company';
import { useQueryData, useSearch } from 'helpers/search';
import type { RemoveMemberSubject } from 'types/modal';
import type { UseSearch } from 'types/search';

type Team = GetTeamResponse;
type TeamMembers = GetTeamMembersResponse;
type ClientSize = NonNullable<Required<Team>['client_size']>;

/** Person id for the executive client (or team lead) on a team, if any. */
export function findExecutiveClientMemberId(
  members: TeamMembers | undefined,
): string | undefined {
  return members?.find((member) =>
    member.roles.some(
      (role) =>
        role.role === 'Executive Client' || role.role === 'Executive Team Lead',
    ),
  )?.id;
}

export const getTeamMemberContext = ({
  member,
  personId,
  teamSlug,
}: {
  member: RemoveMemberSubject | TeamMembers[number];
  personId: string;
  teamSlug: string;
}) => {
  if (member.id === personId) {
    return 'self';
  }

  if (member.pending) {
    return 'pending';
  }

  const isAdmin = member.roles?.find((role) => role.role === 'Banff Admin');
  if (isAdmin) {
    return 'admin';
  }

  const role = member.roles?.find((x) => x.team_?.slug === teamSlug);
  if (role && role.company) {
    const roles = getCompanyTypeRoles(role.company.type);
    if (roles.lead === role.role) {
      return 'lead';
    }
  }

  return undefined;
};

export const useSearchTeams: UseSearch<
  GetTeamsFindResponse,
  Omit<GetTeamsFindData, 'url'>
> = (opts, p) => {
  const { debouncedPhrase, enabled, phrase, ...search } = useSearch(opts);
  const query = useQuery({
    enabled,
    ...getTeamsFindOptions({
      ...p,
      query: {
        ...p?.query,
        phrase: opts?.debounce ? debouncedPhrase : phrase,
      },
    }),
  });
  const queryDataProps = useQueryData({
    ...opts,
    phrase,
    predicate: (team, value) => team.slug === value || team.uuid === value,
    query,
  });

  return {
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    ...query,
    ...search,
    ...queryDataProps,
    debouncedPhrase,
    phrase,
  };
};

/**
 * A team is deactivated when its own flag is off or its company's is — either way
 * it is hidden from search and closed to its members (BAN-5939 / BAN-6000).
 * `active` is optional in the generated types; absent means active.
 */
export const isTeamDeactivated = (team: {
  active?: boolean | null;
  company?: { active?: boolean | null } | null;
}) => team.active === false || team.company?.active === false;

export const getTeamClientSizeField = (value: ClientSize) => {
  switch (value) {
    case 'Both Early Stage and Large/Established':
      return 'both';
    case 'Mostly Early Stage':
      return 'early';
    case 'Mostly Large/Established':
      return 'established';
    // no default
  }
};

/** True when two fund-type selections hold the same values, order aside. */
export const isSameFundType = (
  a: FundType[] | null | undefined,
  b: FundType[] | null | undefined,
): boolean => {
  const left = [...(a ?? [])].sort();
  const right = [...(b ?? [])].sort();
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
};

/**
 * What to persist as a team's fund-type override.
 *
 * The team form is prefilled with the company's values, so a selection
 * identical to the company's is NOT an override — storing it would silently
 * copy the value onto the team, which would then stop following the company if
 * the company ever changed. That case is saved as null, meaning "inherit".
 *
 * Anything else is saved as-is, including an empty selection. Because the field
 * is prefilled, clearing it is a deliberate act and means "this team has no
 * fund type even though its company does" — stored as [], which the API keeps
 * distinct from null.
 */
export const toFundTypeOverride = (
  selected: FundType[] | null | undefined,
  companyFundType: FundType[] | null | undefined,
): FundType[] | null => {
  if (isSameFundType(selected, companyFundType)) {
    return null;
  }
  return selected ?? [];
};
